import { Icon } from '@chakra-ui/icon';
import { Heading, HStack } from '@chakra-ui/layout';
import {
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
} from '@chakra-ui/modal';
import { IconRefresh } from '@tabler/icons';
import { useQueryClient } from '@tanstack/react-query';
import { ButtonComponent } from 'components/buttons/ButtonComponent';
import CheckTable from 'components/dataTables/CheckTable';
import { useLogWebSocketContext } from 'contexts/LogWebSocketContext';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { TbClockShare } from 'react-icons/tb';
import { calculateTimeDifference } from 'utils/functions/dateUtils';

import { columnsDataRecentActivityLog } from './variables/columnsDataRecentActivityLog';

interface IRecentActivityLogModalProps {
	isOpen: boolean;
	onClose: () => void;
}

export const RecentActivityLogModal = (props: IRecentActivityLogModalProps) => {
	const { isOpen, onClose } = props;
	const queryClient = useQueryClient();
	const { isConnected, clearNewLogs, setModalOpen } =
		useLogWebSocketContext();
	const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
	const initialTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const fallbackPollingEnabledRef = useRef(false);

	const tableColumns = useMemo(() => columnsDataRecentActivityLog, []);

	const iconRefreshInvalidationQuery = useCallback(() => {
		queryClient.invalidateQueries({
			queryKey: ['log-list'],
			exact: false,
		});
	}, [queryClient]);

	useEffect(() => {
		setModalOpen(isOpen);
		if (isOpen) {
			clearNewLogs();
		} else {
			clearNewLogs();
		}
		return () => {
			setModalOpen(false);
		};
	}, [isOpen, clearNewLogs, setModalOpen]);

	const renderRow = useCallback(recentActivity => {
		const formattedDate = moment.utc(recentActivity.data_hora).local();
		const timeDifference = calculateTimeDifference(formattedDate);

		return {
			id: recentActivity.id,
			usuario: recentActivity.nome_usuario,
			descricaoAtividade: recentActivity.descricao_acao,
			data: moment
				.utc(recentActivity.data_hora)
				.local()
				.format('DD/MM/YYYY HH:mm:ss'),
			tempoAtividade: timeDifference,
			acao: recentActivity.acao,
			status: recentActivity.status,
			async_job: recentActivity.async_job,
		};
	}, []);

	useEffect(() => {
		if (!isOpen) {
			if (initialTimeoutRef.current) {
				clearTimeout(initialTimeoutRef.current);
				initialTimeoutRef.current = null;
			}
			if (pollingIntervalRef.current) {
				clearInterval(pollingIntervalRef.current);
				pollingIntervalRef.current = null;
			}
			fallbackPollingEnabledRef.current = false;
			return;
		}

		if (isConnected) {
			if (initialTimeoutRef.current) {
				clearTimeout(initialTimeoutRef.current);
				initialTimeoutRef.current = null;
			}
			if (pollingIntervalRef.current) {
				clearInterval(pollingIntervalRef.current);
				pollingIntervalRef.current = null;
			}
			fallbackPollingEnabledRef.current = false;
		} else if (!fallbackPollingEnabledRef.current) {
			fallbackPollingEnabledRef.current = true;
			initialTimeoutRef.current = setTimeout(() => {
				if (!isConnected) {
					iconRefreshInvalidationQuery();
					pollingIntervalRef.current = setInterval(() => {
						iconRefreshInvalidationQuery();
					}, 30000);
				}
			}, 30000);
		}

		return () => {
			if (initialTimeoutRef.current) {
				clearTimeout(initialTimeoutRef.current);
				initialTimeoutRef.current = null;
			}
			if (pollingIntervalRef.current) {
				clearInterval(pollingIntervalRef.current);
				pollingIntervalRef.current = null;
			}
		};
	}, [isOpen, isConnected, iconRefreshInvalidationQuery]);

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			onEsc={onClose}
			size='6xl'
			isCentered
			scrollBehavior={'inside'}
		>
			<ModalOverlay />

			<ModalContent
				borderRadius={20}
				pt='20px'
				minHeight='406px'
			>
				<ModalCloseButton m={'40px 40px 0 0'} />
				<ModalHeader
					display='flex'
					flexDirection='column'
					gap={4}
					color='easyBLUE.300'
				>
					<Icon
						as={TbClockShare}
						color='easyRED.300'
						w={14}
						h={14}
					/>
					<HStack
						alignItems='center'
						spacing={4}
					>
						<Heading
							color='easyBLUE.300'
							mb={2}
							size='md'
						>
							Atividades Recentes
						</Heading>
						<ButtonComponent
							type='icon'
							iconType={<IconRefresh />}
							onClick={iconRefreshInvalidationQuery}
							iconSize={'xs'}
							border='none'
						/>
					</HStack>
				</ModalHeader>

				<ModalBody>
					<CheckTable
						module='log'
						columnsData={tableColumns}
						showGenericSearchInput={false}
						filterModule='log'
						renderRow={renderRow}
						pt='85px'
					/>
				</ModalBody>
				<ModalFooter></ModalFooter>
			</ModalContent>
		</Modal>
	);
};
